import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE, apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { CompanyLogo } from "@/components/CompanyLogo";
import { PoweredBy } from "@/components/PoweredBy";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, MapPin, ShieldCheck, User, Phone,
  Building2, UserRound, Mail, DoorClosed, FileText, Clock, BellRing,
} from "lucide-react";

const PURPOSE_OPTIONS = [
  "Business Meeting", "Interview", "Delivery / Courier", "Personal Visit",
  "Maintenance / Service", "Event / Seminar", "Vendor / Supplier", "Other",
];

const GROUPS = [
  {
    title: "Your Details", icon: User,
    fields: [
      { key: "visitor_name", label: "Your Name", type: "text", ph: "John Mathew", icon: User },
      { key: "visitor_phone", label: "Your Phone Number", type: "tel", ph: "98xxxxxxxx", icon: Phone, opt: true },
      { key: "visitor_email", label: "Your Email", hint: "your visitor pass will be emailed here", type: "email", ph: "you@email.com", icon: Mail },
      { key: "visitor_company", label: "Your Company", type: "text", ph: "ABC Technologies", icon: Building2, opt: true },
    ],
  },
  {
    title: "Who You're Meeting", icon: UserRound,
    fields: [
      { key: "person_to_meet", label: "Meeting Person Name", type: "text", ph: "Arun Kumar", icon: UserRound },
      { key: "person_phone", label: "Meeting Person Phone", type: "tel", ph: "98xxxxxxxx", icon: Phone },
    ],
  },
  {
    title: "Cabin Details", icon: DoorClosed,
    fields: [
      { key: "host_cabin_code", label: "Cabin Code", hint: "as shown on the cabin QR / sticker", type: "text", ph: "WKF-TNP-ST-0001", icon: DoorClosed },
    ],
  },
  {
    title: "Visit Details", icon: FileText,
    fields: [{ key: "purpose", label: "Purpose of Meeting", type: "textarea", ph: "Business meeting", icon: FileText }],
  },
];

const TRUST = [
  { icon: BellRing, title: "Instant host approval", desc: "Your host gets a secure email link the moment you submit." },
  { icon: ShieldCheck, title: "Verified & secure", desc: "Cabin and host details are validated before you're let in." },
  { icon: Clock, title: "Skip the wait", desc: "A digital pass is ready as soon as your host approves." },
];

function Field({ f, value, onChange }) {
  const Icon = f.icon;
  return (
    <div>
      <Label className="text-navy-900 font-medium text-sm flex items-center gap-1">
        <span>{f.label}</span>{!f.opt && <span className="text-rose-500">*</span>}
      </Label>
      {f.hint && <p className="text-xs text-slate-400 mt-0.5">{f.hint}</p>}
      {f.type === "textarea" ? (
        <div className="relative mt-1.5">
          <Icon size={16} className="absolute left-3 top-3.5 text-slate-400" />
          <Textarea required={!f.opt} data-testid={`visitor-${f.key}`} rows={3}
            className="pl-10 resize-none focus-visible:ring-blue-500"
            placeholder={f.ph} value={value || ""} onChange={(e) => onChange(f.key, e.target.value)} />
        </div>
      ) : (
        <div className="relative mt-1.5">
          <Icon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input required={!f.opt} type={f.type} data-testid={`visitor-${f.key}`}
            className="pl-10 h-11 focus-visible:ring-blue-500"
            placeholder={f.ph} value={value || ""} onChange={(e) => onChange(f.key, e.target.value)} />
        </div>
      )}
    </div>
  );
}

function PurposeField({ value, onChange }) {
  const [sel, setSel] = React.useState("");
  const isOther = sel === "Other";
  const pick = (v) => { setSel(v); onChange("purpose", v === "Other" ? "" : v); };
  return (
    <div>
      <Label className="text-navy-900 font-medium text-sm flex items-center gap-1">
        <span>Purpose of Meeting</span><span className="text-rose-500">*</span>
      </Label>
      <Select value={sel} onValueChange={pick}>
        <SelectTrigger data-testid="visitor-purpose" className="mt-1.5 h-11 focus:ring-blue-500">
          <div className="flex items-center gap-2">
            <FileText size={16} className="text-slate-400" />
            <SelectValue placeholder="Select purpose" />
          </div>
        </SelectTrigger>
        <SelectContent>
          {PURPOSE_OPTIONS.map((o) => (
            <SelectItem key={o} value={o} data-testid={`purpose-option-${o.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>{o}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {isOther && (
        <div className="relative mt-3">
          <FileText size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input required data-testid="visitor-purpose-other" className="pl-10 h-11 focus-visible:ring-blue-500"
            placeholder="Please describe your purpose" value={value || ""} onChange={(e) => onChange("purpose", e.target.value)} />
        </div>
      )}
    </div>
  );
}

export default function VisitorRegister() {
  const { publicId } = useParams();
  const [branch, setBranch] = useState(null);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.get(`${API_BASE}/public/branch/${publicId}`)
      .then((r) => setBranch(r.data))
      .catch((e) => setErr(apiError(e)));
  }, [publicId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.visitor_name?.trim()) { toast.error("Please enter your name"); return; }
    if (!form.visitor_email?.trim()) { toast.error("Your email is required"); return; }
    if (!form.person_to_meet?.trim()) { toast.error("Meeting person name is required"); return; }
    if (!form.person_phone?.trim()) { toast.error("Meeting person phone number is required"); return; }
    if (!form.host_cabin_code?.trim()) { toast.error("Cabin code is required"); return; }
    if (!form.purpose || !form.purpose.trim()) { toast.error("Please describe the purpose of meeting"); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_BASE}/public/visitor/register/${publicId}`, form);
      setDone(data);
      toast.success("Request submitted");
    } catch (e2) {
      toast.error(apiError(e2));
    } finally {
      setSubmitting(false);
    }
  };

  if (err) return <PublicError title="Registration Unavailable" message={err} />;
  if (!branch) return <PublicLoading />;

  if (done) return (
    <div className="min-h-screen sf-grid-bg flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8 text-center sf-fade-up" data-testid="visitor-done">
        <div className="w-16 h-16 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-5"><CheckCircle2 size={34} /></div>
        <h2 className="font-display text-2xl font-bold text-navy-900">Request Submitted</h2>
        <p className="text-slate-500 mt-2">{done.message}</p>
        <div className="mt-6 p-4 rounded-xl bg-slate-50 border border-slate-200 text-left">
          <p className="text-xs text-slate-400 uppercase font-semibold tracking-wide">Host Notification</p>
          <p className="text-sm text-navy-900 mt-1">An email with a secure approval link was sent to your host. Please wait while they approve your visit.</p>
        </div>
        <div className="mt-6 flex justify-center"><PoweredBy /></div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen lg:h-screen lg:overflow-hidden bg-slate-100 flex flex-col lg:flex-row">
      {/* Brand panel */}
      <aside className="sf-grid-bg text-white relative overflow-hidden lg:w-[42%] lg:max-w-lg px-8 py-10 lg:p-12 flex flex-col justify-between shrink-0">
        <div className="pointer-events-none absolute -top-16 -right-16 w-64 h-64 rounded-full bg-blue-500/20 blur-3xl" />
        <div className="pointer-events-none absolute bottom-10 -left-10 w-52 h-52 rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="relative z-10">
          <Logo className="h-8" light />
          <div className="mt-10 lg:mt-16">
            <div className="inline-flex items-center gap-2 text-blue-200 text-xs font-semibold uppercase tracking-widest bg-white/10 px-3 py-1.5 rounded-full">
              <MapPin size={13} /> {branch.branch_name}
            </div>
            <h1 className="font-display text-3xl lg:text-4xl font-bold leading-tight mt-4">
              Welcome to<br />{branch.company_name}
            </h1>
            <p className="text-blue-100/80 text-sm lg:text-base mt-3 max-w-sm">
              Check in as a visitor in under a minute. Fill your details and we'll notify your host right away.
            </p>
          </div>
        </div>
        <ul className="relative z-10 mt-10 space-y-4 hidden lg:block">
          {TRUST.map(({ icon: Icon, title, desc }) => (
            <li key={title} className="flex gap-3">
              <span className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center shrink-0"><Icon size={17} className="text-white" /></span>
              <div>
                <p className="font-semibold text-sm text-white">{title}</p>
                <p className="text-blue-100/70 text-xs mt-0.5 leading-relaxed">{desc}</p>
              </div>
            </li>
          ))}
        </ul>
      </aside>

      {/* Form panel */}
      <main className="flex-1 lg:overflow-y-auto">
        <div className="max-w-xl mx-auto px-5 sm:px-8 py-8 lg:py-12">
          <div className="mb-6">
            {branch.logo_url && <CompanyLogo logoUrl={branch.logo_url} className="h-11 mb-4" alt={branch.company_name} />}
            <h2 className="font-display text-2xl font-bold text-navy-900">Visitor Registration</h2>
            <p className="text-slate-500 text-sm mt-1">Fields marked * are required. The cabin member will be notified to approve your visit.</p>
          </div>

          <form onSubmit={submit} className="space-y-7 sf-fade-up">
            {GROUPS.map((g) => {
              const GIcon = g.icon;
              return (
                <section key={g.title} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="w-8 h-8 rounded-lg bg-navy-50 text-navy-900 flex items-center justify-center"><GIcon size={16} /></span>
                    <h3 className="font-display font-semibold text-navy-900">{g.title}</h3>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {g.fields.map((f) => (
                      <div key={f.key} className={f.key === "purpose" ? "sm:col-span-2" : ""}>
                        {f.key === "purpose"
                          ? <PurposeField value={form.purpose} onChange={set} />
                          : <Field f={f} value={form[f.key]} onChange={set} />}
                      </div>
                    ))}
                  </div>
                </section>
              );
            })}

            <Button type="submit" disabled={submitting} data-testid="visitor-submit"
              className="w-full h-12 bg-navy-900 hover:bg-navy-800 font-semibold text-base rounded-xl shadow-lg shadow-navy-900/20 transition-transform active:scale-[.99]">
              {submitting ? <Loader2 className="animate-spin" /> : "Submit Request"}
            </Button>
            <p className="text-xs text-slate-400 flex items-center gap-1.5 justify-center pb-2">
              <ShieldCheck size={13} /> Cabin & host details are validated securely.
            </p>
            <div className="flex justify-center pb-4"><PoweredBy /></div>
          </form>
        </div>
      </main>
    </div>
  );
}

export function PublicLoading() {
  return <div className="min-h-screen sf-grid-bg flex items-center justify-center text-white"><Loader2 className="animate-spin" size={30} /></div>;
}
export function PublicError({ title, message }) {
  return (
    <div className="min-h-screen sf-grid-bg flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8 text-center">
        <h2 className="font-display text-2xl font-bold text-navy-900">{title}</h2>
        <p className="text-slate-500 mt-2">{message}</p>
      </div>
    </div>
  );
}
